import React, { useState, useEffect, useCallback } from "react";
import { getActivityFeed, logActivity } from "../firebase";

const ACTION_META = {
  clip_added:         { icon: "✦", label: "Added clip",            color: "#2B5EA7" },
  clip_removed:       { icon: "✦", label: "Removed clip",          color: "#D93025" },
  clip_status:        { icon: "✦", label: "Updated clip status",   color: "#34d399" },
  transcript_saved:   { icon: "▤", label: "Saved transcript",      color: "#C9A227" },
  transcript_clean:   { icon: "▤", label: "Cleaned transcript",    color: "#C9A227" },
  transcript_upload:  { icon: "▤", label: "Uploaded transcript",   color: "#C9A227" },
  episode_reviewed:   { icon: "✓", label: "Marked reviewed",       color: "#34d399" },
  episode_type_set:   { icon: "◈", label: "Set episode type",      color: "#2B5EA7" },
  episode_unreviewed: { icon: "○", label: "Unmarked reviewed",     color: "#5A6A7E" },
  ai_clips:           { icon: "⬡", label: "AI clip suggestions",   color: "#C9A227" },
  ai_comments:        { icon: "⬡", label: "AI comment analysis",   color: "#C9A227" },
  ai_branding:        { icon: "⬡", label: "AI brand guide",        color: "#C9A227" },
  ai_classify:        { icon: "⬡", label: "AI classification",     color: "#C9A227" },
  channel_refresh:    { icon: "⟳", label: "Channel refreshed",     color: "#2B5EA7" },
  channel_added:      { icon: "◎", label: "Channel connected",     color: "#34d399" },
  csv_export:         { icon: "⬇", label: "Exported CSV",          color: "#5A6A7E" },
};

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function ActivityFeed({ channelId, currentUser }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!channelId) return;
    setLoading(true);
    const data = await getActivityFeed(channelId);
    setEntries(data);
    setLoading(false);
  }, [channelId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const s = {
    wrap: { },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
    title: { fontSize: 13, fontWeight: 700, color: "#E8EBF0", textTransform: "uppercase", letterSpacing: "0.12em" },
    refresh: { background: "none", border: "none", color: "#5A6A7E", cursor: "pointer", fontSize: 14, fontFamily: "inherit" },
    entry: { display: "flex", gap: 12, padding: "10px 0", borderBottom: "1px solid #0D1825" },
    icon: (color) => ({
      width: 28, height: 28, borderRadius: "50%", background: color + "22",
      border: `1px solid ${color}44`, display: "flex", alignItems: "center",
      justifyContent: "center", fontSize: 12, color, flexShrink: 0,
    }),
    info: { flex: 1, minWidth: 0 },
    action: { fontSize: 12, color: "#E8EBF0", fontWeight: 600, marginBottom: 2 },
    detail: { fontSize: 11, color: "#5A6A7E", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    time: { fontSize: 10, color: "#3A4A5E", flexShrink: 0, paddingTop: 2 },
    empty: { textAlign: "center", padding: "32px 0", color: "#3A4A5E", fontSize: 12 },
  };

  if (loading) return (
    <div style={s.empty}>Loading activity...</div>
  );

  if (!entries.length) return (
    <div style={s.empty}>
      <div style={{ fontSize: 24, marginBottom: 8 }}>◎</div>
      No activity yet — actions taken in the app will appear here.
    </div>
  );

  // Group entries by day
  const grouped = [];
  const seenDays = [];
  entries.forEach(e => {
    const day = new Date(e.ts).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});
    if (!seenDays.includes(day)) {
      seenDays.push(day);
      grouped.push({ type:"day", label: day });
    }
    grouped.push({ type:"entry", ...e });
  });

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div style={s.title}>Recent Activity</div>
        <button style={s.refresh} onClick={load} title="Refresh">⟳</button>
      </div>
      {grouped.map((item, i) => {
        if (item.type === "day") return (
          <div key={"day-"+i} style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.14em",color:"#3A4A5E",padding:"12px 0 6px",borderBottom:"1px solid #0D1825",marginBottom:4}}>
            {item.label}
          </div>
        );
        const e = item;
        const meta = ACTION_META[e.type] || { icon: "◎", label: e.type, color: "#5A6A7E" };
        return (
          <div key={e.id} style={s.entry}>
            <div style={s.icon(meta.color)}>{meta.icon}</div>
            <div style={s.info}>
              <div style={s.action}>
                <span style={{color:meta.color}}>{meta.label}</span>
                <span style={{color:"#5A6A7E",fontWeight:400}}> · {e.userName || e.userEmail?.split("@")[0] || "Someone"}</span>
              </div>
              {e.detail && <div style={s.detail}>{e.detail}</div>}
            </div>
            <div style={s.time}>{timeAgo(e.ts)}</div>
          </div>
        );
      })}
    </div>
  );
}

// Hook to log activities from anywhere in the app
export function useActivityLogger(channelId, currentUser) {
  return useCallback(async (type, detail = null) => {
    if (!channelId || !currentUser) return;
    await logActivity(channelId, {
      type,
      detail,
      userEmail: currentUser.email,
      userName: currentUser.displayName || currentUser.email?.split("@")[0],
      userId: currentUser.uid,
    });
  }, [channelId, currentUser]);
}
